import { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import Lenis from "lenis";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Request from "@/pages/Request";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import ModalityPage from "@/pages/ModalityPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Insights from "@/pages/Insights";
import Article from "@/pages/Article";
import AdminDrive from "@/pages/AdminDrive";
import AdminRequests from "@/pages/AdminRequests";
import AdminAnalytics from "@/pages/AdminAnalytics";
import StateRequirements from "@/pages/StateRequirements";
import StatesIndex from "@/pages/StatesIndex";
import ShieldingPlan from "@/pages/ShieldingPlan";
import ShieldingDesign from "@/pages/ShieldingDesign";
import NotFound from "@/pages/NotFound";
import { usePageMeta } from "@/hooks/usePageMeta";
import { trackPageview } from "@/utils/track";

const Tracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageview(pathname);
  }, [pathname]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Meta = ({ title, desc, children }) => {
  usePageMeta(title, desc);
  return children;
};

// Legacy Wix URLs: /post/{slug} -> /insights/{slug} (slugs preserved from the old site).
// Seven old posts have no journal equivalent and get hand-picked destinations.
const LEGACY_POST_REDIRECTS = {
  "contact-heartland-physics": "/contact",
  "mammography-room-shielding-design-guide": "/shielding-design/mammography",
  "mammography-shielding-design-room-planning-barrier-requirements-and-plan-review-tips": "/shielding-design/mammography",
  "veterinary-x-ray-room-shielding-design-guide": "/shielding-design/veterinary",
  "veterinary-x-ray-shielding-design-protecting-staff-in-high-use-small-animal-clinics": "/shielding-design/veterinary",
  "safe-radiation-workspace-solutions": "/insights",
  "understanding-your-shielding-design": "/insights",
};

const LegacyPostRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={LEGACY_POST_REDIRECTS[slug] || `/insights/${slug}`} replace />;
};

// Legacy Wix service/landing pages → closest new equivalent (keeps their Google rankings flowing).
const LEGACY_PAGE_REDIRECTS = {
  "/blog": "/insights",
  "/contact-us": "/contact",
  "/articles": "/shielding-design",
  "/book-online": "/contact",
  "/services": "/",
  "/courses": "/",
  "/dosimetry-monitoring": "/",
  "/radiation-safety-consulting": "/",
  "/radiation-safety-training": "/",
  "/alabama-medical-facilities": "/shielding-requirements/alabama",
  "/iac-alabama": "/shielding-requirements/alabama",
  "/ct-shielding-design": "/shielding-design/ct",
  "/shielding-design/shielding-design-request-form": "/request",
  "/shielding-design/x-ray-shielding-design": "/shielding-design/diagnostic-x-ray",
  "/shielding-design/ct-shielding-design": "/shielding-design/ct",
  "/shielding-design/fluoroscopy-shielding-design": "/shielding-design/fluoroscopy-c-arms",
  "/shielding-design/mammography-shielding-design": "/shielding-design/mammography",
  "/shielding-design/dental-shielding-design": "/shielding-design/dental",
  "/shielding-design/nuclear-medicine-shielding-design": "/",
  "/shielding-design/research-laboratory-shielding-design": "/",
  "/shielding-design/industrial-shielding-design": "/",
  "/radiation-shielding-design/x-ray-shielding-design": "/shielding-design/diagnostic-x-ray",
  "/radiation-shielding-design/ct-shielding-design": "/shielding-design/ct",
  "/radiation-shielding-design/dental-shielding-design": "/shielding-design/dental",
  "/radiation-shielding-design/nuclear-medicine-shielding-design": "/",
  "/radiation-shielding-design/research-laboratory-shielding-design": "/",
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Tracker />
      <div className="min-h-screen bg-paper text-ink font-body antialiased">
        <Nav />
        <main>
          <Routes>
            <Route path="/" element={<Meta title="Radiation Shielding Design & Plan Review | Heartland Physics" desc="Physicist-prepared shielding designs for X-ray, CT, fluoroscopy, dental, mammography, and veterinary rooms. State-specific reports and one-day quotes."><Home /></Meta>} />
            <Route path="/request" element={<Meta title="Request a Shielding Design | Heartland Physics" desc="Start your radiation shielding project: structured intake, floor plan upload, and secure payment. Five-business-day reports prepared directly by the physicist."><Request /></Meta>} />
            <Route path="/about" element={<Meta title="About Heartland Physics | Diagnostic X-Ray Shielding Design" desc="A physicist-led consulting practice focused on radiation shielding design for diagnostic imaging. Work directly with Lancer Smith, M.S."><About /></Meta>} />
            <Route path="/contact" element={<Meta title="Contact Heartland Physics | Diagnostic X-Ray Shielding Design" desc="Email Lancer@heartlandphysics.com or request a shielding design — a direct line to the physicist on every project."><Contact /></Meta>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<Article />} />
            <Route path="/shielding-design" element={<Meta title="Radiation Shielding Design for X-Ray, CT & Imaging Rooms | Heartland Physics" desc="Physicist-prepared radiation shielding design for diagnostic imaging: NCRP 147 barrier calculations, marked-up floor plans, and a state-ready signed report — $950 per room, five business days."><ShieldingDesign /></Meta>} />
            <Route path="/shielding-design/:slug" element={<ModalityPage />} />
            <Route path="/shielding-plan" element={<Meta title="Shielding Plan for X-Ray, CT & Imaging Rooms | Heartland Physics" desc="What a shielding plan includes, when you need one, and how to get a physicist-signed radiation shielding plan — $950 per room, five-business-day turnaround."><ShieldingPlan /></Meta>} />
            <Route path="/admin/drive" element={<AdminDrive />} />
            <Route path="/admin/requests" element={<AdminRequests />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/shielding-requirements" element={<StatesIndex />} />
            <Route path="/shielding-requirements/:slug" element={<StateRequirements />} />
            <Route path="/post/:slug" element={<LegacyPostRedirect />} />
            {Object.entries(LEGACY_PAGE_REDIRECTS).map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
